/**
 * @file ProducerEditDialog.tsx
 * @description Create / edit производителя (phase08.5 shadcn).
 */

import { useEffect, useState } from "react";

import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { useNotify } from "../ui/NotifyProvider";
import { catalogProducers, type CatalogProducer } from "../../lib/api-catalog";
import { ApiError } from "../../lib/api-client";

interface Props {
  open: boolean;
  producer: CatalogProducer | null;
  initialName?: string;
  onClose: () => void;
  onSaved: (producer: CatalogProducer) => void;
}

export function ProducerEditDialog({
  open,
  producer,
  initialName,
  onClose,
  onSaved,
}: Props) {
  const notify = useNotify();
  const isEdit = !!producer;
  const [displayName, setDisplayName] = useState("");
  const [legalAddress, setLegalAddress] = useState("");
  const [inn, setInn] = useState("");
  const [ogrn, setOgrn] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (producer) {
      setDisplayName(producer.displayName);
      setLegalAddress(producer.legalAddress);
      setInn(producer.inn ?? "");
      setOgrn(producer.ogrn ?? "");
      setContactPhone(producer.contactPhone ?? "");
      setContactEmail(producer.contactEmail ?? "");
      setNotes(producer.notes ?? "");
    } else {
      setDisplayName(initialName ?? "");
      setLegalAddress("");
      setInn("");
      setOgrn("");
      setContactPhone("");
      setContactEmail("");
      setNotes("");
    }
    setError(null);
  }, [producer, initialName, open]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!displayName.trim()) {
      setError("Название обязательно.");
      return;
    }
    if (!legalAddress.trim()) {
      setError("Юридический адрес обязателен (ст.9 п.1.2 ФЗ-2300-1).");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        displayName: displayName.trim(),
        legalAddress: legalAddress.trim(),
        inn: inn.trim() || undefined,
        ogrn: ogrn.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
        notes: notes.trim() || undefined,
      };
      const saved = producer
        ? await catalogProducers.update(producer.id, payload)
        : await catalogProducers.create(payload);
      notify.success(producer ? "Производитель обновлён" : "Производитель создан");
      onSaved(saved);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось сохранить");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent fullScreen className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Редактировать производителя" : "Новый производитель"}
          </DialogTitle>
        </DialogHeader>

        <form
          id="producer-form"
          onSubmit={(e) => void onSubmit(e)}
          className="flex flex-col gap-3 overflow-y-auto"
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="p-name">Название *</Label>
            <Input
              id="p-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={submitting}
              required
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="p-addr">Юридический адрес *</Label>
            <Textarea
              id="p-addr"
              value={legalAddress}
              onChange={(e) => setLegalAddress(e.target.value)}
              disabled={submitting}
              required
              rows={2}
            />
            <p className="text-xs text-on-surface-variant">
              ст.9 п.1.2 ФЗ-2300-1 — обязателен для карточки товара
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1 flex flex-col gap-1.5">
              <Label htmlFor="p-inn">ИНН</Label>
              <Input
                id="p-inn"
                value={inn}
                onChange={(e) => setInn(e.target.value)}
                disabled={submitting}
              />
            </div>
            <div className="flex-1 flex flex-col gap-1.5">
              <Label htmlFor="p-ogrn">ОГРН</Label>
              <Input
                id="p-ogrn"
                value={ogrn}
                onChange={(e) => setOgrn(e.target.value)}
                disabled={submitting}
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1 flex flex-col gap-1.5">
              <Label htmlFor="p-phone">Контакт. телефон</Label>
              <Input
                id="p-phone"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                disabled={submitting}
              />
            </div>
            <div className="flex-1 flex flex-col gap-1.5">
              <Label htmlFor="p-email">Контакт. email</Label>
              <Input
                id="p-email"
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                disabled={submitting}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="p-notes">Внутренние заметки</Label>
            <Textarea
              id="p-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={submitting}
              rows={2}
            />
          </div>
          {error ? <Alert severity="error">{error}</Alert> : null}
        </form>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Отмена
          </Button>
          <Button type="submit" form="producer-form" disabled={submitting}>
            {submitting ? "Сохраняем…" : isEdit ? "Сохранить" : "Создать"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
